/**
 * panelViews.ts - Tab panel renderers
 */

import type { GameStateData } from '../game/state.js';
import { getSelectedHauntedBuilding } from '../game/state.js';
import { GHOST_DEFS, REGULAR_GHOST_IDS, PRO_GHOST_IDS } from '../game/data/ghosts.js';
import { ROOM_TYPES, ALL_ROOM_TYPE_IDS } from '../game/data/rooms.js';
import { getTrainingQuote } from '../game/data/trainings.js';
import { FACILITY_DEFS, ALL_FACILITY_IDS, facilityCostForLevel } from '../game/data/facilities.js';
import { CONFIG } from '../game/config.js';
import { t } from '../i18n/index.js';
import { formatSp } from './formatters.js';

// ── Ghost Roster Panel ──────────────────────────────────────

export type GhostClickCallback = (ghostInstanceId: string) => void;

export function renderGhostRoster(
  container: HTMLElement,
  state: GameStateData,
  onGhostClick: GhostClickCallback,
): void {
  container.innerHTML = '';

  if (state.ghosts.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'reviews-empty';
    empty.textContent = t('not_enough_sp');
    container.appendChild(empty);
    return;
  }

  for (const ghost of state.ghosts) {
    const def = GHOST_DEFS[ghost.characterId];
    if (!def) continue;

    const card = document.createElement('div');
    card.className = 'ghost-card';

    // Status classes for left bar indicator
    if (ghost.fatigue >= 100) {
      card.classList.add('fatigued');
    } else if (ghost.training || ghost.academySession) {
      card.classList.add('training');
    } else if (ghost.assignedRoomId) {
      card.classList.add('assigned');
    } else if (ghost.role === 'lobby_staff' || ghost.role === 'facility_staff') {
      card.classList.add('assigned');
    } else {
      card.classList.add('idle');
    }

    // Avatar
    const avatar = document.createElement('div');
    avatar.className = 'ghost-avatar';
    const avatarImg = document.createElement('img');
    avatarImg.className = 'ghost-avatar-img';
    avatarImg.src = def.image;
    avatarImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
    avatarImg.draggable = false;
    avatar.appendChild(avatarImg);
    card.appendChild(avatar);

    // Info
    const info = document.createElement('div');
    info.className = 'ghost-info';

    const nameLine = document.createElement('div');
    nameLine.className = 'ghost-info-name';
    nameLine.textContent = t(def.nameKey as Parameters<typeof t>[0]);

    const gradeBadge = document.createElement('span');
    gradeBadge.className = `grade-badge grade-${def.grade}`;
    gradeBadge.textContent = def.grade;
    nameLine.appendChild(gradeBadge);
    info.appendChild(nameLine);

    const statusLine = document.createElement('div');
    statusLine.className = 'ghost-status-line';
    let assignedText;
    if (ghost.training) {
      assignedText = t('ghost_training_in_progress');
    } else if (ghost.academySession) {
      assignedText = t('academy_in_progress');
    } else if (ghost.role === 'lobby_staff') {
      assignedText = t('ghost_role_lobby_staff');
    } else if (ghost.role === 'facility_staff') {
      assignedText = t('ghost_role_facility_staff');
    } else {
      assignedText = ghost.assignedRoomId ? t('ghost_assigned') : t('ghost_idle');
    }
    const expRequired = Math.floor(
      CONFIG.ghostLevelUpExpBase * Math.pow(CONFIG.ghostLevelUpExpGrowth, ghost.level - 1),
    );
    statusLine.textContent = `${t('ghost_level', { n: ghost.level })} | ${assignedText} | ${t('ghost_exp', { cur: ghost.experience, max: expRequired })}`;
    info.appendChild(statusLine);

    // Training bonus display
    if (ghost.trainingBonus > 0) {
      const bonusLine = document.createElement('div');
      bonusLine.style.cssText = 'font-size:10px;color:#34d399;margin-top:2px;';
      bonusLine.textContent = t('training_bonus', { pct: Math.round(ghost.trainingBonus * 100) }) + ` (${t('training_count', { n: ghost.trainingCount })})`;
      info.appendChild(bonusLine);
    }

    // Training progress
    if (ghost.training) {
      const now = Date.now();
      const total = ghost.training.completesAt - ghost.training.startedAt;
      const elapsed = Math.min(now - ghost.training.startedAt, total);
      const pct = Math.round((elapsed / total) * 100);

      const trainingBar = document.createElement('div');
      trainingBar.className = 'ghost-exp-bar';
      trainingBar.style.marginTop = '4px';
      const trainingFill = document.createElement('div');
      trainingFill.className = 'ghost-exp-fill';
      trainingFill.style.width = `${pct}%`;
      trainingFill.style.background = 'linear-gradient(90deg, #fbbf24, #f59e0b)';
      trainingBar.appendChild(trainingFill);
      info.appendChild(trainingBar);

      // Comedy quote
      const quoteKey = getTrainingQuote(ghost.characterId, ghost.training.trainingId);
      if (quoteKey) {
        const quote = document.createElement('div');
        quote.className = 'ghost-quote';
        quote.textContent = `"${t(quoteKey as Parameters<typeof t>[0])}"`;
        info.appendChild(quote);
      }
    } else {
      // Fatigue bar
      const fatigueBar = document.createElement('div');
      fatigueBar.className = 'ghost-fatigue-bar';
      const fatigueFill = document.createElement('div');
      fatigueFill.className = 'ghost-fatigue-fill';
      fatigueFill.style.width = `${ghost.fatigue}%`;
      fatigueBar.appendChild(fatigueFill);
      info.appendChild(fatigueBar);

      // EXP bar
      const expBar = document.createElement('div');
      expBar.className = 'ghost-exp-bar';
      const expFill = document.createElement('div');
      expFill.className = 'ghost-exp-fill';
      expFill.style.width = `${Math.min(100, (ghost.experience / expRequired) * 100)}%`;
      expBar.appendChild(expFill);
      info.appendChild(expBar);

      // Quote
      const quoteKey = ghost.fatigue >= 100 ? def.fatiguedQuoteKey : def.quoteKey;
      const quote = document.createElement('div');
      quote.className = 'ghost-quote';
      quote.textContent = `"${t(quoteKey as Parameters<typeof t>[0])}"`;
      info.appendChild(quote);
    }

    card.appendChild(info);
    card.addEventListener('click', () => onGhostClick(ghost.id));
    container.appendChild(card);
  }
}

// ── Building Controls Panel ──────────────────────────────────

export type BuildActionCallback = (action: 'buy_floor' | 'open_manage_popup') => void;

export function renderBuildingControls(
  container: HTMLElement,
  state: GameStateData,
  onAction: BuildActionCallback,
): void {
  container.innerHTML = '';

  // === Manage Business Button (opens popup) ===
  const manageBtn = document.createElement('button');
  manageBtn.className = state.isOpen
    ? 'build-action-btn manage-open'
    : 'build-action-btn manage-closed';

  const manageLabelDiv = document.createElement('div');
  manageLabelDiv.className = 'btn-title';
  manageLabelDiv.textContent = state.isOpen ? t('toggle_close') : t('manage_business');
  manageBtn.appendChild(manageLabelDiv);

  const manageSubDiv = document.createElement('div');
  manageSubDiv.className = 'cost-label';
  manageSubDiv.textContent = state.isOpen
    ? `Day ${state.dayNumber} | ${t('day_summary_visitors', { n: state.todayVisitors })} | ${formatSp(state.todayEarnings)} SP`
    : `Day ${state.dayNumber} | ${t('ticket_price_label', { n: state.ticketPrice })}`;
  manageBtn.appendChild(manageSubDiv);

  manageBtn.addEventListener('click', () => onAction('open_manage_popup'));
  container.appendChild(manageBtn);

  // === Buy Floor Button ===
  const nextFloorCount = state.building.length + 1;
  const floorCosts = CONFIG.floorCosts;
  const floorCost = nextFloorCount < floorCosts.length
    ? floorCosts[nextFloorCount]
    : floorCosts[floorCosts.length - 1] * Math.pow(5, nextFloorCount - floorCosts.length + 1);

  const canAffordFloor = state.sp >= floorCost;

  const btn = document.createElement('button');
  btn.className = 'build-action-btn buy-floor-btn';
  btn.disabled = !canAffordFloor;

  const costText = document.createElement('div');
  costText.className = 'btn-title';
  costText.textContent = t('buy_floor');
  btn.appendChild(costText);

  const costLabel = document.createElement('div');
  costLabel.className = 'cost-label';
  costLabel.textContent = `${formatSp(floorCost)} SP`;
  btn.appendChild(costLabel);

  btn.addEventListener('click', () => onAction('buy_floor'));
  container.appendChild(btn);

  // Ad boost indicator
  const now = Date.now();
  const hasBoost = state.adBoosts.some((b) => b.type === 'sp_double' && b.expiresAt > now);
  if (hasBoost) {
    const boostIndicator = document.createElement('div');
    boostIndicator.className = 'boost-indicator';
    boostIndicator.textContent = t('ad_active_boost');
    container.appendChild(boostIndicator);
  }
}

// ── Shop Panel ───────────────────────────────────────────────

export type ShopActionCallback = (action: 'hire_ghost', payload: { ghostCharacterId: string }) => void;
export type ShopRoomCallback = (action: 'unlock_room_type', payload: { roomTypeId: string }) => void;

export function renderShop(
  container: HTMLElement,
  state: GameStateData,
  onGhostAction: ShopActionCallback,
  onRoomAction: ShopRoomCallback,
  options?: { hideRoomTypes?: boolean },
): void {
  container.innerHTML = '';

  // Regular Ghosts section
  const ghostTitle = document.createElement('div');
  ghostTitle.className = 'shop-section-title';
  ghostTitle.textContent = t('shop_ghosts');
  container.appendChild(ghostTitle);

  for (const ghostId of REGULAR_GHOST_IDS) {
    const def = GHOST_DEFS[ghostId];
    const alreadyHired = state.ghosts.some((g) => g.characterId === ghostId);

    const item = document.createElement('div');
    item.className = 'shop-item';

    const shopGhostAvatar = document.createElement('div');
    shopGhostAvatar.className = 'shop-ghost-avatar';
    const shopGhostImg = document.createElement('img');
    shopGhostImg.className = 'shop-ghost-img';
    shopGhostImg.src = def.image;
    shopGhostImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
    shopGhostImg.draggable = false;
    shopGhostAvatar.appendChild(shopGhostImg);
    item.appendChild(shopGhostAvatar);

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = `${def.grade} - ${t(def.nameKey as Parameters<typeof t>[0])}`;
    infoEl.appendChild(nameEl);

    const subEl = document.createElement('div');
    subEl.className = 'shop-item-sub';
    subEl.textContent = t(def.originKey as Parameters<typeof t>[0]);
    infoEl.appendChild(subEl);

    const costEl = document.createElement('div');
    costEl.className = 'shop-item-cost';
    costEl.textContent = `${formatSp(def.hireCost)} SP`;
    infoEl.appendChild(costEl);

    item.appendChild(infoEl);

    const btn = document.createElement('button');
    btn.className = 'shop-btn';
    if (alreadyHired) {
      btn.classList.add('purchased');
      btn.disabled = true;
      btn.textContent = t('already_hired');
    } else {
      btn.disabled = state.sp < def.hireCost;
      btn.textContent = t('hire');
      btn.addEventListener('click', () => onGhostAction('hire_ghost', { ghostCharacterId: ghostId }));
    }
    item.appendChild(btn);
    container.appendChild(item);
  }

  // Professional Ghosts section
  const proTitle = document.createElement('div');
  proTitle.className = 'shop-section-title';
  proTitle.style.marginTop = '8px';
  proTitle.textContent = t('shop_pro_ghosts');
  container.appendChild(proTitle);

  for (const ghostId of PRO_GHOST_IDS) {
    const def = GHOST_DEFS[ghostId];
    const alreadyHired = state.ghosts.some((g) => g.characterId === ghostId);

    const item = document.createElement('div');
    item.className = 'shop-item shop-item-pro';

    const shopGhostAvatar = document.createElement('div');
    shopGhostAvatar.className = 'shop-ghost-avatar';
    const shopGhostImg = document.createElement('img');
    shopGhostImg.className = 'shop-ghost-img';
    shopGhostImg.src = def.image;
    shopGhostImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
    shopGhostImg.draggable = false;
    shopGhostAvatar.appendChild(shopGhostImg);
    item.appendChild(shopGhostAvatar);

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = `${def.grade} - ${t(def.nameKey as Parameters<typeof t>[0])}`;
    const proBadge = document.createElement('span');
    proBadge.className = 'pro-badge';
    proBadge.textContent = t('pro_ghost_badge');
    nameEl.appendChild(proBadge);
    infoEl.appendChild(nameEl);

    const subEl = document.createElement('div');
    subEl.className = 'shop-item-sub';
    subEl.textContent = t(def.originKey as Parameters<typeof t>[0]);
    infoEl.appendChild(subEl);

    if (def.uniqueAbilityKey) {
      const abilityEl = document.createElement('div');
      abilityEl.className = 'shop-item-ability';
      abilityEl.textContent = t(def.uniqueAbilityKey as Parameters<typeof t>[0]);
      infoEl.appendChild(abilityEl);
    }

    const costEl = document.createElement('div');
    costEl.className = 'shop-item-cost';
    costEl.textContent = `${formatSp(def.hireCost)} SP`;
    infoEl.appendChild(costEl);

    item.appendChild(infoEl);

    const btn = document.createElement('button');
    btn.className = 'shop-btn';
    if (alreadyHired) {
      btn.classList.add('purchased');
      btn.disabled = true;
      btn.textContent = t('already_hired');
    } else {
      btn.disabled = state.sp < def.hireCost;
      btn.textContent = t('hire');
      btn.addEventListener('click', () => onGhostAction('hire_ghost', { ghostCharacterId: ghostId }));
    }
    item.appendChild(btn);
    container.appendChild(item);
  }

  // Room Types section (skip if hidden, e.g. in recruitment tab)
  if (options?.hideRoomTypes) return;

  const roomTitle = document.createElement('div');
  roomTitle.className = 'shop-section-title';
  roomTitle.style.marginTop = '8px';
  roomTitle.textContent = t('shop_rooms');
  container.appendChild(roomTitle);

  const currentThemeId = getSelectedHauntedBuilding(state).themeId;
  const filteredRoomIds = currentThemeId
    ? ALL_ROOM_TYPE_IDS.filter(id => ROOM_TYPES[id].themeTags.includes(currentThemeId))
    : ALL_ROOM_TYPE_IDS;

  for (const roomTypeId of filteredRoomIds) {
    const def = ROOM_TYPES[roomTypeId];
    if (def.unlockCost === 0) continue;

    const alreadyUnlocked = state.unlockedRoomTypes.includes(roomTypeId);

    const item = document.createElement('div');
    item.className = 'shop-item';

    // Room preview image
    const shopRoomAvatar = document.createElement('div');
    shopRoomAvatar.className = 'shop-room-avatar';
    const shopRoomImg = document.createElement('img');
    shopRoomImg.className = 'shop-room-img';
    shopRoomImg.src = def.imagePath;
    shopRoomImg.alt = t(def.nameKey as Parameters<typeof t>[0]);
    shopRoomImg.draggable = false;
    if (!alreadyUnlocked) {
      shopRoomImg.style.filter = 'grayscale(1) brightness(0.5)';
    }
    shopRoomAvatar.appendChild(shopRoomImg);
    item.appendChild(shopRoomAvatar);

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    infoEl.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'shop-item-sub';
    descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
    infoEl.appendChild(descEl);

    const costEl = document.createElement('div');
    costEl.className = 'shop-item-cost';
    costEl.textContent = `${formatSp(def.unlockCost)} SP`;
    infoEl.appendChild(costEl);

    // Show unlock conditions
    if (!alreadyUnlocked && def.unlockCondition) {
      const condEl = document.createElement('div');
      condEl.className = 'shop-item-conditions';
      const parts: string[] = [];
      if (def.unlockCondition.minDay !== undefined) {
        const met = state.dayNumber >= def.unlockCondition.minDay;
        parts.push(`${met ? '✓' : '✗'} ${t('unlock_requires_day', { n: def.unlockCondition.minDay })}`);
      }
      if (def.unlockCondition.minFame !== undefined) {
        const met = state.fame >= def.unlockCondition.minFame;
        parts.push(`${met ? '✓' : '✗'} ${t('unlock_requires_fame', { n: def.unlockCondition.minFame })}`);
      }
      if (def.unlockCondition.requiredRoom !== undefined) {
        const met = state.unlockedRoomTypes.includes(def.unlockCondition.requiredRoom);
        const reqName = t(ROOM_TYPES[def.unlockCondition.requiredRoom]?.nameKey as Parameters<typeof t>[0] ?? '');
        parts.push(`${met ? '✓' : '✗'} ${t('unlock_requires_room', { name: reqName })}`);
      }
      condEl.textContent = parts.join(' · ');
      infoEl.appendChild(condEl);
    }

    item.appendChild(infoEl);

    // Check if unlock conditions are met
    let conditionsMet = true;
    if (!alreadyUnlocked && def.unlockCondition) {
      const { minDay, minFame, requiredRoom } = def.unlockCondition;
      if (minDay !== undefined && state.dayNumber < minDay) conditionsMet = false;
      if (minFame !== undefined && state.fame < minFame) conditionsMet = false;
      if (requiredRoom !== undefined && !state.unlockedRoomTypes.includes(requiredRoom)) conditionsMet = false;
    }

    const btn = document.createElement('button');
    btn.className = 'shop-btn';
    if (alreadyUnlocked) {
      btn.classList.add('purchased');
      btn.disabled = true;
      btn.textContent = t('already_unlocked');
    } else if (!conditionsMet) {
      btn.disabled = true;
      btn.textContent = t('unlock_condition_not_met');
    } else {
      btn.disabled = state.sp < def.unlockCost;
      btn.textContent = t('unlock_room_type');
      btn.addEventListener('click', () => onRoomAction('unlock_room_type', { roomTypeId }));
    }
    item.appendChild(btn);
    container.appendChild(item);
  }
}

// ── Facilities & Ad Rewards Panel ────────────────────────────

export type FacilityActionCallback = (action: 'build' | 'upgrade', facilityId: string) => void;
export type AdRewardCallback = (action: 'sp_boost' | 'vip_visitor') => void;

export function renderFacilities(
  container: HTMLElement,
  state: GameStateData,
  onFacilityAction: FacilityActionCallback,
  onAdReward: AdRewardCallback,
): void {
  container.innerHTML = '';

  // Ad Rewards section
  const adTitle = document.createElement('div');
  adTitle.className = 'shop-section-title';
  adTitle.textContent = t('ad_rewards_title');
  container.appendChild(adTitle);

  // SP Boost ad
  const spBoostItem = document.createElement('div');
  spBoostItem.className = 'shop-item';
  const spBoostInfo = document.createElement('div');
  spBoostInfo.className = 'shop-item-info';
  const spBoostName = document.createElement('div');
  spBoostName.className = 'shop-item-name';
  spBoostName.textContent = t('ad_sp_boost');
  spBoostInfo.appendChild(spBoostName);
  const spBoostDesc = document.createElement('div');
  spBoostDesc.className = 'shop-item-sub';
  spBoostDesc.textContent = t('ad_sp_boost_desc');
  spBoostInfo.appendChild(spBoostDesc);
  spBoostItem.appendChild(spBoostInfo);
  const spBoostBtn = document.createElement('button');
  spBoostBtn.className = 'shop-btn ad-btn';
  spBoostBtn.textContent = t('ad_watch');
  spBoostBtn.addEventListener('click', () => onAdReward('sp_boost'));
  spBoostItem.appendChild(spBoostBtn);
  container.appendChild(spBoostItem);

  // VIP Visitor ad
  const vipItem = document.createElement('div');
  vipItem.className = 'shop-item';
  const vipInfo = document.createElement('div');
  vipInfo.className = 'shop-item-info';
  const vipName = document.createElement('div');
  vipName.className = 'shop-item-name';
  vipName.textContent = t('ad_vip_visitor');
  vipInfo.appendChild(vipName);
  const vipDesc = document.createElement('div');
  vipDesc.className = 'shop-item-sub';
  vipDesc.textContent = t('ad_vip_visitor_desc');
  vipInfo.appendChild(vipDesc);
  vipItem.appendChild(vipInfo);
  const vipBtn = document.createElement('button');
  vipBtn.className = 'shop-btn ad-btn';
  vipBtn.textContent = t('ad_watch');
  vipBtn.addEventListener('click', () => onAdReward('vip_visitor'));
  vipItem.appendChild(vipBtn);
  container.appendChild(vipItem);

  // Facilities section
  const facTitle = document.createElement('div');
  facTitle.className = 'shop-section-title';
  facTitle.style.marginTop = '8px';
  facTitle.textContent = t('facilities_title');
  container.appendChild(facTitle);

  for (const facilityId of ALL_FACILITY_IDS) {
    const def = FACILITY_DEFS[facilityId];
    const existing = state.facilities.find((f) => f.facilityId === facilityId);

    const item = document.createElement('div');
    item.className = 'shop-item';

    const infoEl = document.createElement('div');
    infoEl.className = 'shop-item-info';

    const nameEl = document.createElement('div');
    nameEl.className = 'shop-item-name';
    nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    if (existing) {
      nameEl.textContent += ` ${t('facility_level', { n: existing.level })}`;
    }
    infoEl.appendChild(nameEl);

    const descEl = document.createElement('div');
    descEl.className = 'shop-item-sub';
    descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
    infoEl.appendChild(descEl);

    item.appendChild(infoEl);

    const btn = document.createElement('button');
    btn.className = 'shop-btn';

    if (!existing) {
      const cost = def.buildCost;
      btn.disabled = state.sp < cost;
      btn.textContent = t('facility_build', { sp: formatSp(cost) });
      btn.addEventListener('click', () => onFacilityAction('build', facilityId));
    } else if (existing.level < def.maxLevel) {
      const cost = facilityCostForLevel(facilityId as any, existing.level + 1);
      btn.disabled = state.sp < cost;
      btn.textContent = t('facility_upgrade', { sp: formatSp(cost) });
      btn.addEventListener('click', () => onFacilityAction('upgrade', facilityId));
    } else {
      btn.classList.add('purchased');
      btn.disabled = true;
      btn.textContent = t('prop_max');
    }

    item.appendChild(btn);
    container.appendChild(item);
  }
}

// ── Reviews Panel ────────────────────────────────────────────

export function renderReviews(
  container: HTMLElement,
  state: GameStateData,
): void {
  container.innerHTML = '';

  if (state.reviews.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'reviews-empty';
    empty.textContent = t('reviews_empty');
    container.appendChild(empty);
    return;
  }

  // Average rating
  const avgStars = state.reviews.reduce((sum, r) => sum + r.stars, 0) / state.reviews.length;
  const avgEl = document.createElement('div');
  avgEl.className = 'reviews-avg';
  avgEl.textContent = t('reviews_avg', { n: avgStars.toFixed(1) });
  container.appendChild(avgEl);

  for (const review of state.reviews) {
    const card = document.createElement('div');
    card.className = 'review-card';

    const starsEl = document.createElement('div');
    starsEl.className = 'review-stars';
    // Use text stars instead of emoji
    const starText = Array(review.stars).fill('*').join('');
    const emptyStarText = Array(5 - review.stars).fill('.').join('');
    starsEl.textContent = `[${starText}${emptyStarText}]`;
    card.appendChild(starsEl);

    const textEl = document.createElement('div');
    textEl.className = 'review-text';
    let reviewText = t(review.textKey as Parameters<typeof t>[0]);
    if (review.ghostNameKey) {
      reviewText = reviewText.replace('{ghost}', t(review.ghostNameKey as Parameters<typeof t>[0]));
    }
    textEl.textContent = reviewText;
    card.appendChild(textEl);

    container.appendChild(card);
  }
}

// ── Prestige Panel ───────────────────────────────────────────

export type PrestigeCallback = () => void;

export function renderPrestige(
  container: HTMLElement,
  state: GameStateData,
  onPrestige: PrestigeCallback,
): void {
  container.innerHTML = '';

  const req = CONFIG.prestigeMinFloors;
  const reqFame = CONFIG.prestigeMinFame;
  const reqSp = CONFIG.prestigeMinTotalSp;

  const metFloors = state.building.length >= req;
  const metFame = state.fame >= reqFame;
  const metSp = state.totalSpEarned >= reqSp;
  const canPrestige = metFloors && metFame && metSp;

  const makeReqRow = (label: string, met: boolean) => {
    const row = document.createElement('div');
    row.className = `prestige-req${met ? ' met' : ''}`;
    row.textContent = label;
    const check = document.createElement('span');
    check.textContent = met ? 'OK' : 'X';
    row.appendChild(check);
    container.appendChild(row);
  };

  makeReqRow(t('prestige_requirement_floors', { n: req }), metFloors);
  makeReqRow(t('prestige_requirement_fame', { n: reqFame }), metFame);
  makeReqRow(t('prestige_requirement_sp', { n: formatSp(reqSp) }), metSp);

  if (state.prestigeLevel > 0) {
    const lvlInfo = document.createElement('div');
    lvlInfo.className = 'prestige-info';
    lvlInfo.textContent = t('prestige_level', { n: state.prestigeLevel });
    container.appendChild(lvlInfo);
  }

  const btn = document.createElement('button');
  btn.className = 'prestige-btn';
  btn.disabled = !canPrestige;
  btn.textContent = t('prestige_confirm');
  btn.addEventListener('click', onPrestige);
  container.appendChild(btn);
}
